// ROOM 404: Secure CSV Export Utility
// Implements RFC 4180 compliance and spreadsheet formula injection protection (CWE-1236)

export interface InviteCsvRow {
  code: string;
  deepLink: string;
  maxUses: number;
  expiration: string | number;
  cohort: string;
}

/**
 * Sanitizes a CSV cell to prevent spreadsheet formula injection.
 * Prepends a single quote (') if the cell begins with =, +, -, @, \t, or \r.
 * Encloses the cell in double quotes and escapes any double quotes within.
 */
export function sanitizeCsvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return '""';
  }

  let str = String(value);

  // Formula injection prevention: characters that trigger execution in Excel/Calc/Sheets
  if (/^[=+\-@\t\r]/.test(str)) {
    str = "'" + str;
  }

  // RFC 4180: double-quote escaping
  str = str.replace(/"/g, '""');
  return `"${str}"`;
}

/**
 * Builds a secure CSV string from invite rows with required headers.
 */
export function generateInvitesCsv(rows: InviteCsvRow[]): string {
  const headers = ['Code', 'Deep Link', 'Max Uses', 'Expiration', 'Cohort'];
  const headerLine = headers.map((h) => sanitizeCsvCell(h)).join(',');

  const lines = rows.map((row) => {
    const formattedExpiration =
      typeof row.expiration === 'number'
        ? new Date(row.expiration).toISOString()
        : String(row.expiration);

    return [
      sanitizeCsvCell(row.code),
      sanitizeCsvCell(row.deepLink),
      sanitizeCsvCell(row.maxUses),
      sanitizeCsvCell(formattedExpiration),
      sanitizeCsvCell(row.cohort),
    ].join(',');
  });

  return [headerLine, ...lines].join('\r\n');
}

/**
 * Triggers a browser download of the CSV content.
 */
export function triggerCsvDownload(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
